import { ref } from 'vue'
import type { ChatFilePreview } from 'models'
import type { ChatFilePreviewTarget } from './utils'

/**
 * The preview of a Word document or a spreadsheet stored in a chat (spec
 * `chat-office-files.md`, F6, items 3 and 4): the file it shows, its content
 * once read, and the read. `read` asks the server for one stored file of the
 * chat on screen. A version never changes, so each one is read once until
 * `reset`, which a view calls when it shows another chat.
 */
export function useChatFilePreview(
    read: (storedFileName: string) => Promise<ChatFilePreview>,
) {
    const target = ref<ChatFilePreviewTarget>()
    const preview = ref<ChatFilePreview>()
    const loading = ref(false)
    const error = ref(false)
    const cache = new Map<string, ChatFilePreview>()
    /**
     * Counts every `open` and `close`: only the answer of the open still on
     * screen reaches the view. Told apart by call, not by file: the same
     * file opened again while its first read is out took that read's failure
     * as its own and said "unavailable" over the content.
     */
    let lastOpen = 0
    /** Bumped by `reset`: a read of the chat before is not kept. */
    let chatGeneration = 0

    const open = async (next: ChatFilePreviewTarget) => {
        const call = ++lastOpen
        const generation = chatGeneration
        target.value = next
        error.value = false
        const cached = cache.get(next.storedFileName)
        preview.value = cached
        // Another preview may still be loading: this one is not.
        loading.value = false
        if (cached) {
            return
        }
        loading.value = true
        const current = () => call === lastOpen
        try {
            const data = await read(next.storedFileName)
            if (generation === chatGeneration) {
                cache.set(next.storedFileName, data)
            }
            if (current()) {
                preview.value = data
            }
        } catch {
            if (current()) {
                error.value = true
            }
        } finally {
            if (current()) {
                loading.value = false
            }
        }
    }

    const close = () => {
        target.value = undefined
        lastOpen += 1
    }

    const reset = () => {
        close()
        cache.clear()
        chatGeneration += 1
    }

    return { target, preview, loading, error, open, close, reset }
}
